import { FC } from "react";
import styled from "styled-components";
import { P2, ParagraphProps } from "./Paragraph";
import { color as themeColor } from "@styles";
import { createArray, roundToNearestHalf } from "@utils";
import { Anchor } from "./Anchor";

interface Props {
  inverted?: boolean;
  score?: number;
}

export const Star = ({ isLarge = false }: { isLarge?: boolean }) => (
  <StarSVG
    width={isLarge ? 48 : 16}
    viewBox="0 0 18 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.0001 12.175L12.6501 11.25L14.1751 15.95L9.0001 12.175ZM17.4001 6.10005H10.9751L9.0001 0.0500488L7.0251 6.10005H0.600098L5.8001 9.85005L3.8251 15.9L9.0251 12.15L12.2251 9.85005L17.4001 6.10005Z"
      fill="white"
    />
  </StarSVG>
);

const StarSVG = styled.svg<{ isLarge?: boolean }>`
  @media only screen and (max-width: 30rem) {
    max-width: 16px;
  }
`;

export const TrustpilotStar = ({
  index,
  trustScore,
  showExactScore = false,
  isLarge = false,
  isTransparent = false,
}: {
  isLarge?: boolean;
  index: number;
  trustScore: number;
  showExactScore?: boolean;
  isTransparent?: boolean;
}) => {
  const percent = index + 1 < trustScore ? 1 : trustScore - index;
  const backgroundColor = isTransparent ? "transparent" : "#00b67a";

  return (
    <Square
      to50={index + 0.5 <= trustScore ? backgroundColor : "#dcdce6"}
      to100={index + 1 <= trustScore ? backgroundColor : "#dcdce6"}
      last={index === 4}
      percent={showExactScore ? percent : 0.5}
    >
      <Star isLarge={isLarge} />
    </Square>
  );
};

export const Trustpilot: FC<React.PropsWithChildren<Props>> = ({
  score = 4.7,
  inverted = false,
}) => {
  const trustScore = roundToNearestHalf(score);

  return (
    <Container>
      <Flex>
        {createArray(5).map((element, index) => (
          <TrustpilotStar key={`trustpilot-star-${index}`} index={index} trustScore={trustScore} />
        ))}
      </Flex>
      <TextSpacer />
      <ModifiedP2 pt="1rem" inverted={inverted}>
        Our customers say{" "}
        <Anchor
          href="https://uk.trustpilot.com/review/www.yulife.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          <strong>Excellent on Trustpilot</strong>
        </Anchor>
      </ModifiedP2>
    </Container>
  );
};

const Flex = styled.div`
  display: inline-flex;
  gap: 0.125rem;
`;

const Container = styled.div`
  padding-top: 2rem;
`;

const TextSpacer = styled.div`
  display: inline-block;
  padding-top: 0;
`;

const Square = styled.div<{ to50: string; to100: string; last?: boolean; percent: number }>`
  display: inline-block;
  background: linear-gradient(
    to right,
    ${(p) => p.to50 || "#fff"} ${(p) => p.percent * 100 || 50}%,
    ${(p) => p.to100 || "#fff"} ${(p) => p.percent * 100 || 50}%
  );
  padding: 0.4rem 0.2875rem 0.05rem 0.2875rem;
`;

const ModifiedP2 = styled(P2)<ParagraphProps & { inverted?: boolean }>`
  display: inline-block;
  line-height: 1.25rem;
  margin-left: 1rem;
  color: ${(p) => (p.inverted ? "#fff" : themeColor.B1100)};

  @media only screen and (max-width: 65rem) {
    display: block;
    margin-left: 0;
  }
`;
